import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, BuildingIcon, InfoIcon, PlusIcon, MoreHorizontalIcon, EditIcon } from 'lucide-react';
export function PayoutSettings() {
  const navigate = useNavigate();
  const merchantName = 'Siam Trading Co';
  // State for Minimum Balance section
  const [minBalanceEnabled, setMinBalanceEnabled] = useState(false);
  const [minBalanceAmount, setMinBalanceAmount] = useState('');
  const [descriptor, setDescriptor] = useState('');
  const isMinBalanceInvalid = minBalanceEnabled && (!minBalanceAmount || Number(minBalanceAmount) <= 0);
  return <div className="max-w-5xl mx-auto py-8 px-4" data-id="element-9793">
      {/* Header */}
      <div className="flex items-center gap-3 mb-8" data-id="element-9794">
        <button onClick={() => navigate('/settings')} className="p-2 -ml-2 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-colors" aria-label="Back to settings" data-id="element-9795">
          <ArrowLeftIcon size={20} data-id="element-9796" />
        </button>
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-9797">Payout settings</h1>
      </div>

      <div className="space-y-6" data-id="element-9798">
        {/* SECTION 1: Settlement currencies and bank accounts */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm" data-id="element-9799">
          <div className="p-6 border-b border-gray-200" data-id="element-9800">
            <h2 className="text-lg font-semibold text-gray-900 mb-1" data-id="element-9801">
              Settlement currencies and bank accounts
            </h2>
            <p className="text-sm text-gray-500" data-id="element-9802">
              To accumulate a balance in a new currency, add it here. Attach a
              bank account to pay out the balance.
            </p>
          </div>

          <div className="overflow-x-auto" data-id="element-9803">
            <table className="w-full text-left border-collapse" data-id="element-9804">
              <thead data-id="element-9805">
                <tr className="bg-gray-50/50 border-b border-gray-200" data-id="element-9806">
                  <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider w-32" data-id="element-9807">
                    Currency
                  </th>
                  <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9808">
                    Payout bank account
                  </th>
                  <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider w-48" data-id="element-9809">
                    <div className="flex items-center gap-1.5" data-id="element-9810">
                      Payout fee
                      <InfoIcon size={14} className="text-gray-400" data-id="element-9811" />
                    </div>
                  </th>
                  <th className="px-6 py-3 w-24" data-id="element-9812"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200" data-id="element-9813">
                <tr className="hover:bg-gray-50/50 transition-colors" data-id="element-9814">
                  <td className="px-6 py-4" data-id="element-9815">
                    <div className="flex items-center gap-2" data-id="element-9816">
                      <span className="text-sm font-medium text-gray-900" data-id="element-9817">
                        THB
                      </span>
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium bg-emerald-100 text-emerald-800" data-id="element-9818">
                        Default
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4" data-id="element-9819">
                    <div className="flex items-center gap-3" data-id="element-9820">
                      <div className="w-8 h-8 rounded bg-gray-100 border border-gray-200 flex items-center justify-center flex-shrink-0" data-id="element-9821">
                        <BuildingIcon size={16} className="text-gray-500" data-id="element-9822" />
                      </div>
                      <div className="flex items-center gap-2" data-id="element-9823">
                        <span className="text-sm text-gray-900" data-id="element-9824">
                          KASIKORN BANK
                        </span>
                        <span className="text-sm text-gray-500 font-mono" data-id="element-9825">
                          ••••0536 / 7375-001
                        </span>
                        <span className="text-lg leading-none" title="Thailand" data-id="element-9826">
                          🇹🇭
                        </span>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4" data-id="element-9827">
                    <span className="text-sm text-gray-600" data-id="element-9828">FREE</span>
                  </td>
                  <td className="px-6 py-4 text-right" data-id="element-9829">
                    <div className="flex items-center justify-end gap-2" data-id="element-9830">
                      <button className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors shadow-sm" data-id="element-9831">
                        Edit
                      </button>
                      <button className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors border border-transparent" data-id="element-9832">
                        <MoreHorizontalIcon size={18} data-id="element-9833" />
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="p-4 border-t border-gray-200 bg-gray-50/30" data-id="element-9834">
            <button className="text-sm font-medium text-indigo-600 hover:text-indigo-700 transition-colors" data-id="element-9835">
              Add a settlement currency
            </button>
          </div>
        </div>

        {/* SECTION 2: Minimum balance */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm" data-id="element-9836">
          <div className="p-6 border-b border-gray-200" data-id="element-9837">
            <h2 className="text-lg font-semibold text-gray-900 mb-1" data-id="element-9838">
              Minimum balance
            </h2>
            <p className="text-sm text-gray-500" data-id="element-9839">
              Keep a minimum amount in your balance so that funds are available
              in case of refunds, disputes, and fees.
            </p>
          </div>

          <div className="p-6" data-id="element-9840">
            <div className="flex items-start gap-4" data-id="element-9841">
              <button type="button" role="switch" aria-checked={minBalanceEnabled} onClick={() => setMinBalanceEnabled(!minBalanceEnabled)} className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:ring-offset-2 mt-0.5 ${minBalanceEnabled ? 'bg-indigo-600' : 'bg-gray-200'}`} data-id="element-9842">
                <span aria-hidden="true" className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${minBalanceEnabled ? 'translate-x-5' : 'translate-x-0'}`} data-id="element-9843" />
              </button>

              <div className="flex-1" data-id="element-9844">
                <label className="text-sm font-medium text-gray-900 cursor-pointer block" onClick={() => setMinBalanceEnabled(!minBalanceEnabled)} data-id="element-9845">
                  Keep a minimum amount in your payments balance
                </label>
                <p className="text-sm text-gray-500 mt-0.5 mb-4" data-id="element-9846">
                  We will adjust your payouts so that you maintain a minimum
                  balance.
                </p>

                {minBalanceEnabled && <div className="mb-4" data-id="element-9847">
                    <div className="relative max-w-xs" data-id="element-9848">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-9849">
                        <span className="text-gray-500 sm:text-sm" data-id="element-9850">THB</span>
                      </div>
                      <input type="number" value={minBalanceAmount} onChange={e => setMinBalanceAmount(e.target.value)} className={`block w-full pl-12 pr-3 py-2 sm:text-sm rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 ${isMinBalanceInvalid ? 'border-red-300 text-red-900 placeholder-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300'}`} placeholder="0" data-id="element-9851" />
                    </div>
                    {isMinBalanceInvalid && <p className="mt-2 text-sm text-red-600 flex items-center gap-1.5" data-id="element-9852">
                        <InfoIcon size={14} data-id="element-9853" />
                        The amount must be greater than 0.
                      </p>}
                  </div>}

                <button className="flex items-center gap-1.5 text-sm font-medium text-gray-400 hover:text-gray-600 transition-colors" data-id="element-9854">
                  <PlusIcon size={16} data-id="element-9855" />
                  Add another currency
                </button>
              </div>
            </div>
          </div>

          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3" data-id="element-9856">
            <button className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors shadow-sm" data-id="element-9857">
              Cancel
            </button>
            <button disabled={isMinBalanceInvalid} className={`px-4 py-2 text-sm font-medium text-white rounded-md transition-colors shadow-sm ${isMinBalanceInvalid ? 'bg-indigo-400 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700'}`} data-id="element-9858">
              Save
            </button>
          </div>
        </div>

        {/* SECTION 3: Payout statement descriptor */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm" data-id="element-9859">
          <div className="p-6 border-b border-gray-200" data-id="element-9860">
            <h2 className="text-lg font-semibold text-gray-900 mb-1" data-id="element-9861">
              Payout statement descriptor
            </h2>
            <p className="text-sm text-gray-500" data-id="element-9862">
              Select a statement descriptor for payouts that appear on your bank
              statement.
            </p>
          </div>

          <div className="p-6 space-y-6" data-id="element-9863">
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 flex items-start gap-3" data-id="element-9864">
              <InfoIcon size={20} className="text-gray-500 flex-shrink-0 mt-0.5" data-id="element-9865" />
              <p className="text-sm text-gray-700 leading-relaxed" data-id="element-9866">
                You can set a custom statement descriptor when manually creating
                a payout from{' '}
                <button className="text-indigo-600 hover:text-indigo-800 hover:underline" data-id="element-9867">
                  Balances
                </button>
                , overriding this account-level setting.
              </p>
            </div>

            <div data-id="element-9868">
              <label className="block text-sm font-medium text-gray-900 mb-2" data-id="element-9869">
                Statement Descriptor
              </label>
              <input type="text" value={descriptor} onChange={e => setDescriptor(e.target.value)} placeholder={`Example - ${merchantName}`} className="block w-full max-w-md px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" data-id="element-9870" />
            </div>
          </div>

          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3" data-id="element-9871">
            <button className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors shadow-sm" data-id="element-9872">
              Cancel
            </button>
            <button className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md transition-colors shadow-sm" data-id="element-9873">
              Save
            </button>
          </div>
        </div>
      </div>
    </div>;
}